// Push Notification Service for LuxosGroup HR

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

class PushNotificationService {
  constructor() {
    this.swRegistration = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  async init() {
    if (!this.isSupported) {
      console.warn('Push notifications not supported');
      return false;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered');
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  async getVapidKey(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/push/vapid-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      return null;
    }
  }

  async subscribe(token) {
    if (!this.swRegistration) {
      await this.init();
    }

    try {
      // Check permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return { success: false, error: 'Permission denied' };
      }

      // Get VAPID key
      const vapidKey = await this.getVapidKey(token);
      if (!vapidKey) {
        return { success: false, error: 'Failed to get VAPID key' };
      }

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(vapidKey);

      // Subscribe
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to server
      const response = await fetch(`${API_BASE_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(subscription.toJSON())
      });

      if (response.ok) {
        console.log('Push subscription saved');
        return { success: true, subscription };
      } else {
        return { success: false, error: 'Failed to save subscription' };
      }
    } catch (error) {
      console.error('Push subscription error:', error);
      return { success: false, error: error.message };
    }
  }

  async unsubscribe(token) {
    if (!this.swRegistration) {
      return { success: false, error: 'No service worker' };
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (!subscription) {
        return { success: true, message: 'No subscription' };
      }

      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from server
      await fetch(`${API_BASE_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      return { success: true };
    } catch (error) {
      console.error('Unsubscribe error:', error);
      return { success: false, error: error.message };
    }
  }

  async getSubscriptionStatus() {
    if (!this.swRegistration) {
      await this.init();
    }

    if (!this.swRegistration) {
      return { supported: false, subscribed: false, permission: 'default' };
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      return {
        supported: true,
        subscribed: !!subscription,
        permission: Notification.permission
      };
    } catch (error) {
      return { supported: true, subscribed: false, permission: Notification.permission };
    }
  }

  async sendTestNotification(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/push/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      return await response.json();
    } catch (error) {
      return { error: error.message };
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export const pushService = new PushNotificationService();
export default pushService;
